import { FormProvider, useForm } from 'react-hook-form'
import {
  BackIcon,
  CurecallButton,
  EArrowPosition,
  ECurecallButtonAppearance,
  ECurecallNotificationAppearance,
} from '@honestica/curecall-ui'
import { useTranslation } from 'react-i18next'
import { useEffect, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useNotification } from 'src/providers/notification.provider'
import { ChatbotService } from 'src/services/chatbot.service'
import type { TChatbotResponse } from './chatbot-api.type'
import { getErrorMessage } from 'src/utils/http.util'
import { ChatbotNameFields } from './chatbot-name-fields.component'
import { ChatbotScheduleFields } from './chatbot-schedule-fields.component'
import { ChatbotMedEventField } from './chatbot-medevent-field.component'
import { ChatbotTogglesFields } from './chatbot-options/chatbot-toggles-fields.component'
import { ChatbotDashboardSection } from './chatbot-options/chatbot-dashboard-section.component'
import {
  ETriggerTiming,
  ETriggerUnit,
  TChatbotGeneralFormValues,
  DEFAULT_ALLOWED_DAYS,
} from './chatbot-form.type'
import { transformChatbotFormToApiPayload } from './chatbot.helper'
import { parseApiToFormValues } from './chatbot-api.transformer'
import { useDebouncedCallback } from 'use-debounce'
import { useLocalizedNavigate } from 'src/hooks/use-localized-navigate'
import { WithFeatureEnabled, useCurrentProject } from 'src/providers/current-project.provider'
import { useFollowupIdFromParams } from '../../../_components/use-followup-id-from-params.hook'

export function ChatbotGeneralForm({
  chatbotToUpdate,
  onCancel,
  onNameChange,
}: Readonly<{
  chatbotToUpdate?: TChatbotResponse
  onCancel: () => void
  onNameChange?: (name: string) => void
}>) {
  const { t } = useTranslation()
  const { project } = useCurrentProject()
  const followupId = useFollowupIdFromParams()
  const { notify } = useNotification()
  const navigate = useLocalizedNavigate()
  const queryClient = useQueryClient()
  const isEditMode = !!chatbotToUpdate

  const methods = useForm<TChatbotGeneralFormValues>({
    defaultValues:
      isEditMode && chatbotToUpdate
        ? parseApiToFormValues(chatbotToUpdate)
        : {
            name: '',
            description: '',
            schedule: {
              triggerDelay: 0,
              triggerUnit: ETriggerUnit.DAYS,
              triggerTiming: ETriggerTiming.AFTER,
              sendAt: undefined,
              allowedDays: DEFAULT_ALLOWED_DAYS,
              medeventListIds: [],
              avoidHolidays: false,
            },
            options: {
              automaticEnd: null,
              requestHumanReview: null,
              automaticRelaunchDelay: null,
            },
          },
  })
  const { handleSubmit, watch } = methods
  const [isButtonLoading, setIsButtonLoading] = useState<boolean>(false)

  const debouncedNameChange = useDebouncedCallback((name: string) => {
    if (!onNameChange) return
    if (name.trim()) {
      onNameChange(name)
    } else {
      onNameChange(t('pages.configuration.followups.chatbot.general-form.creating-agent'))
    }
  }, 300)

  useEffect(() => {
    // Only watch name changes in create mode (for timeline preview)
    if (!isEditMode) {
      const subscription = watch((value, { name: fieldName }) => {
        if (fieldName === 'name') {
          debouncedNameChange(value.name || '')
        }
      })
      return () => subscription.unsubscribe()
    }
  }, [watch, debouncedNameChange, isEditMode])

  const onSubmit = async (formData: TChatbotGeneralFormValues) => {
    setIsButtonLoading(true)
    try {
      const apiPayload = transformChatbotFormToApiPayload(formData)

      if (isEditMode && chatbotToUpdate) {
        // Save changes
        const updatedChatbot = await ChatbotService.update(
          project.id,
          followupId,
          chatbotToUpdate.id,
          apiPayload,
        )

        // Publish the chatbot
        await ChatbotService.publish(project.id, followupId, chatbotToUpdate.id, true)

        // Update chatbot cache with the response from the API
        queryClient.setQueryData(['chatbot', chatbotToUpdate.id], updatedChatbot)

        notify({
          appearance: ECurecallNotificationAppearance.SUCCESS,
          title: t('common.actions.success'),
          message: t('pages.configuration.followups.chatbot.publish-success'),
        })

        // Redirect to followup page
        navigate(`/${project.id}/configuration/followups/${followupId}`)
      } else {
        // Add nodes structure for creation when expirationMessage is provided
        if (apiPayload.options?.automaticEnd?.expirationMessage) {
          apiPayload.nodes = {
            Start: {
              actions: [],
              connections: [],
            },
          }
        }

        const createdChatbot = await ChatbotService.create(project.id, followupId, apiPayload)
        notify({
          appearance: ECurecallNotificationAppearance.SUCCESS,
          title: t('common.actions.success'),
          message: t('pages.configuration.followups.chatbot.general-form.create-success'),
        })
        navigate(
          `/${project.id}/configuration/followups/${followupId}/chatbot/${createdChatbot.id}/update-nodes`,
          { replace: true },
        )
      }
    } catch (err) {
      notify({
        appearance: ECurecallNotificationAppearance.ERROR,
        title: t('common.error.title'),
        message: getErrorMessage(err),
      })
    } finally {
      setIsButtonLoading(false)
    }
  }

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)} className="c-w-full c-overflow-y-scroll">
        <div className="c-flex c-flex-col c-gap-[26px]">
          <ChatbotNameFields />
          <ChatbotScheduleFields />
          <ChatbotMedEventField />
          <WithFeatureEnabled featuresAccepted={['dashboard', 'dashboard-v2']}>
            <ChatbotDashboardSection />
          </WithFeatureEnabled>
          <ChatbotTogglesFields />
        </div>

        <div className="c-mt-8 c-mb-6 c-pb-10 c-flex c-justify-center c-gap-4">
          <CurecallButton
            appearance={ECurecallButtonAppearance.CANCEL}
            type="button"
            isLoading={isButtonLoading}
            onClick={onCancel}
          >
            {t('common.actions.cancel')}
          </CurecallButton>
          {isEditMode && chatbotToUpdate && (
            <CurecallButton
              type="button"
              appearance={ECurecallButtonAppearance.SECONDARY}
              isLoading={isButtonLoading}
              onClick={() =>
                navigate(
                  `/${project.id}/configuration/followups/${followupId}/chatbot/${chatbotToUpdate.id}/update-nodes`,
                )
              }
            >
              {t('pages.configuration.followups.chatbot.general-form.go-to-step-2')}
              <BackIcon position={EArrowPosition.TO_RIGHT} className="c-h-3 c-w-3 c-ml-1 c-inline-block" />
            </CurecallButton>
          )}
          <CurecallButton type="submit" isLoading={isButtonLoading}>
            {isEditMode
              ? t('pages.configuration.followups.chatbot.general-form.save-and-publish')
              : t('pages.configuration.followups.chatbot.general-form.submit-button')}
          </CurecallButton>
        </div>
      </form>
    </FormProvider>
  )
}
